// NavSection.tsx — site navigation header for the master template.
// Desktop: standard, split (flank), stacked, minimal.
// Mobile + Minimal open a right-side drawer; More uses an inline dropdown.

import React from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import type { ThemeTokens, TemplateNavigateFn, SectionOptions, CustomWebsitePage } from "../types";
import { useCreatorData } from "../templateData";
import { E, EL, useEdit } from "../InlineEdit";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { narrowTemplateLayout } from "../resolveTheme";
import { TEMPLATE_LAYOUT_MOBILE_MAX_PX } from "../templatePreviewDevice";
import { tplThemeVars } from "../templateThemeVars";
import { getCreatorSpaRelativePath } from "../../../../Utils/url/getCreatorSpaRelativePath";
import { isPublicWebsiteTemplateRoute } from "../../../../Utils/websiteTemplateGuards";
import { IoClose, IoMenu, IoSearchOutline } from "react-icons/io5";
import {
  NAV_OMIT_EDIT_KEY,
  NAV_ORDER_EDIT_KEY,
  clampNavLabel,
  defaultSlugForNavKey,
  parseNavOmit,
  parseNavOrder,
  resolveNavDesktopLayout,
  resolveNavHref,
  resolveNavLabel,
  resolveNavMobileLayout,
  SITE_HEADER_NAV_DEFAULTS,
  splitNavOverflowWithBudget,
  type SiteHeaderNavKey,
} from "../websiteEditorInspectorTypes";
import { useWebsiteEditorInspectorOptional } from "../WebsiteEditorInspectorContext";
import { isNavOmitted } from "../websiteNavOmit";
import {
  BLOG_NAV_DISABLED_TIP,
  isBlogNavChromeLocked,
} from "../websiteEditorNav";
import { useShopCatalog } from "../useShopCatalog";
import { SHOP_BROWSE_OTHER_SLUG } from "../shopBreadcrumbs";

interface NavProps {
  T: ThemeTokens;
  page?: string;
  name?: string;
  style?: string;
  mobileStyle?: string;
  onNavigate?: TemplateNavigateFn;
  customPages?: CustomWebsitePage[];
}

export function NavSection({
  T,
  page,
  name = "STUDIO",
  style = "standard",
  mobileStyle,
  onNavigate,
  customPages = [],
}: NavProps) {
  const CREATOR = useCreatorData();
  const editorCtx = useWebsiteEditorInspectorOptional();
  const { get, editable } = useEdit();
  const location = useLocation();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [overflowOpen, setOverflowOpen] = React.useState(false);
  const [minimalOpen, setMinimalOpen] = React.useState(false);
  /** Desktop: column mega-menu hanging under PRODUCTS. */
  const [shopCatsOpen, setShopCatsOpen] = React.useState(false);
  /** Optimistic active link so the underline flips on click, before pageKey catches up. */
  const [pendingNavKey, setPendingNavKey] = React.useState<string | null>(null);
  const desktopLayoutPreview = resolveNavDesktopLayout(style);
  /** Mobile hamburger + Minimal panel share the right drawer (More is a dropdown). */
  const drawerOpen = mobileMenuOpen || minimalOpen;
  const navLinksRef = React.useRef<HTMLElement | null>(null);
  const navHeaderRef = React.useRef<HTMLElement | null>(null);
  const [navBudgetPx, setNavBudgetPx] = React.useState<number | null>(null);
  /**
   * Live derives mobile from measured page width. The dashboard preview can stay
   * on `T.device=desktop` (Desktop toggle / scaled frame) while the canvas is
   * phone-narrow — that paints the desktop "More ▾" overflow instead of the
   * hamburger + drawer/fullscreen menu. Match live when the page is < 890px.
   */
  const [widthForcedMobile, setWidthForcedMobile] = React.useState(false);
  const closeDrawer = React.useCallback(() => {
    setMobileMenuOpen(false);
    setMinimalOpen(false);
  }, []);
  const closeOverflow = React.useCallback(() => setOverflowOpen(false), []);
  const closeShopCats = React.useCallback(() => setShopCatsOpen(false), []);
  React.useEffect(() => {
    setPendingNavKey(null);
  }, [page]);
  React.useEffect(() => {
    closeDrawer();
    closeOverflow();
    closeShopCats();
  }, [page, location.pathname, closeDrawer, closeOverflow, closeShopCats]);
  // Keep Minimal closed until tapped — drawer covers the page (unlike the old dropdown panel).
  React.useEffect(() => {
    setMinimalOpen(false);
  }, [editable, desktopLayoutPreview, page]);
  const isNavActive = React.useCallback(
    (k: string) => (pendingNavKey != null ? pendingNavKey === k : page === k),
    [pendingNavKey, page],
  );
  React.useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    const onOther = () => closeDrawer();
    window.dispatchEvent(new Event("mte-we-exclusive-open"));
    window.addEventListener("keydown", onKey);
    window.addEventListener("mte-we-exclusive-open", onOther);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mte-we-exclusive-open", onOther);
    };
  }, [drawerOpen, closeDrawer]);
  React.useEffect(() => {
    if (!overflowOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOverflow();
    };
    const onDoc = () => closeOverflow();
    window.addEventListener("keydown", onKey);
    // Next tick so the More button click that opened it does not immediately close.
    const t = window.setTimeout(() => {
      window.addEventListener("click", onDoc);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("click", onDoc);
    };
  }, [overflowOpen, closeOverflow]);
  // Drawer: public → body (full viewport). Editor → preview frame so it
  // doesn't cover Tweaks / chrome. Lock the matching scroll container.
  React.useEffect(() => {
    if (!drawerOpen) return;
    if (editable) {
      const root = document.getElementById("master-template-scroll-root");
      if (root) {
        const prev = root.style.overflow;
        root.style.overflow = "hidden";
        return () => {
          root.style.overflow = prev;
        };
      }
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, editable]);
  React.useLayoutEffect(() => {
    const header = navHeaderRef.current;
    const page =
      (header?.closest(".mte-tpl-page") as HTMLElement | null) ||
      (header?.closest("[data-tpl-device]") as HTMLElement | null) ||
      header;
    if (!page || typeof ResizeObserver === "undefined") {
      setWidthForcedMobile(false);
      return;
    }
    const pageIsMobileWidth = () => {
      const w = page.getBoundingClientRect().width;
      return Number.isFinite(w) && w > 0 && w < TEMPLATE_LAYOUT_MOBILE_MAX_PX;
    };
    const applyWidth = () => {
      const narrow = pageIsMobileWidth();
      setWidthForcedMobile(narrow);
      if (narrow) setNavBudgetPx(null);
    };
    let raf = 0;
    const scheduleWidth = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyWidth);
    };
    scheduleWidth();
    const pageRo = new ResizeObserver(scheduleWidth);
    pageRo.observe(page);

    // Theme mobile, width-forced mobile, or Minimal: no desktop More budget.
    if (
      narrowTemplateLayout(T.device) ||
      pageIsMobileWidth() ||
      desktopLayoutPreview === "minimal"
    ) {
      setNavBudgetPx(null);
      return () => {
        cancelAnimationFrame(raf);
        pageRo.disconnect();
      };
    }
    const linksEl = navLinksRef.current;
    const target = header || linksEl;
    if (!target) {
      return () => {
        cancelAnimationFrame(raf);
        pageRo.disconnect();
      };
    }
    const apply = () => {
      if (pageIsMobileWidth()) {
        applyWidth();
        return;
      }
      setWidthForcedMobile(false);
      // Stacked: links are full-width under the wordmark — measure the row.
      if (desktopLayoutPreview === "stacked" && linksEl) {
        setNavBudgetPx(linksEl.clientWidth);
        return;
      }
      if (header) {
        const wordmark = header.querySelector(
          ".mte-tpl-nav-wordmark",
        ) as HTMLElement | null;
        const gutter = 48;
        const markW = wordmark?.offsetWidth || 0;
        setNavBudgetPx(Math.max(0, header.clientWidth - markW - gutter));
        return;
      }
      if (linksEl) setNavBudgetPx(linksEl.clientWidth);
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(target);
    if (linksEl && linksEl !== target) ro.observe(linksEl);
    return () => {
      cancelAnimationFrame(raf);
      pageRo.disconnect();
      ro.disconnect();
    };
  }, [T.device, desktopLayoutPreview, style, mobileStyle]);

  const useMobileNavChrome =
    narrowTemplateLayout(T.device) || widthForcedMobile;
  const navHandle =
    CREATOR.bio.handle ||
    (CREATOR.bio.username
      ? `@${CREATOR.bio.username.trim().toLowerCase()}`
      : name);
  const linkKeyToPage: Record<string, string> = {
    home: "home",
    shop: "shop",
    blog: "journal",
    content: "content",
    linkInBio: "linkInBio",
    about: "about",
    press: "press",
    contact: "contact",
    search: "search",
  };
  const go = (k: string, label: string) => (e: React.MouseEvent) => {
    if (onNavigate) {
      e.preventDefault();
      // Paint the active state in this click frame — don't wait for pageKey.
      setPendingNavKey(k);
      // Track before navigate so attribution is not racing URL rewrite.
      templateAnalytics?.trackNavClick(label, linkKeyToPage[k] || k);
      onNavigate(linkKeyToPage[k] || k);
    }
  };
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const userId = CREATOR.bio?.userId || "";
  // Mega-menu is disabled; warm categories only when the drawer opens (minimal/
  // mobile side column) or after idle so home first paint isn't competing.
  const [shopCatalogWarm, setShopCatalogWarm] = React.useState(false);
  // Paint the drawer first. Enabling the catalog in the same tap as the
  // hamburger starts a products RPC and stalls the open animation on iPhone.
  React.useEffect(() => {
    if (!drawerOpen || shopCatalogWarm) return;
    const id = window.requestAnimationFrame(() => setShopCatalogWarm(true));
    return () => window.cancelAnimationFrame(id);
  }, [drawerOpen, shopCatalogWarm]);
  React.useEffect(() => {
    if (shopCatalogWarm) return;
    let cancelled = false;
    type IdleWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as IdleWindow;
    let idleId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const warm = () => {
      if (!cancelled) setShopCatalogWarm(true);
    };
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(warm, { timeout: 5000 });
    } else {
      timeoutId = setTimeout(warm, 2500);
    }
    return () => {
      cancelled = true;
      if (idleId != null) w.cancelIdleCallback?.(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [shopCatalogWarm]);
  const shopCatalog = useShopCatalog({
    username,
    userId,
    enabled: Boolean(username && userId) && shopCatalogWarm,
    shopView: "all",
    categorySlug: null,
    shopBrowse: {},
    surface: "products",
    skipGrid: true,
  });
  /** Breadcrumb primaries for PRODUCTS hover + mobile drill-in. */
  const shopCategories = React.useMemo(
    () =>
      shopCatalog.browseIndex.primaries.filter(
        (p) => p.slug !== SHOP_BROWSE_OTHER_SLUG && String(p.slug || "").trim(),
      ),
    [shopCatalog.browseIndex.primaries],
  );
  const goShopAll = (e: React.MouseEvent) => {
    closeShopCats();
    closeDrawer();
    if (!onNavigate) return;
    e.preventDefault();
    setPendingNavKey("shop");
    templateAnalytics?.trackNavClick("Products", "shop");
    onNavigate("shop", { shopView: "all", shopBrowse: {} });
  };
  const goShopCategory = (slug: string, label: string) => (e: React.MouseEvent) => {
    closeShopCats();
    closeDrawer();
    if (!onNavigate) return;
    e.preventDefault();
    setPendingNavKey("shop");
    templateAnalytics?.trackShopBrowse({
      level: "primary",
      label,
      primarySlug: slug,
      secondarySlug: null,
      subSlug: null,
      brandSlug: null,
    });
    templateAnalytics?.trackNavClick(label, "shop");
    onNavigate("shop", {
      shopView: "all",
      shopBrowse: {
        primarySlug: slug,
        secondarySlug: null,
        subSlug: null,
        brandSlug: null,
      },
    });
  };
  const navWordmarkLabel = get("nav.name", navHandle);
  const navWordmark = editable ? (
    <E path="nav.name" className="mte-tpl-nav-wordmark-txt">
      {navHandle}
    </E>
  ) : onNavigate ? (
    <button
      type="button"
      className="mte-tpl-nav-wordmark-btn"
      onClick={go("home", "Home")}
    >
      <span className="mte-tpl-nav-wordmark-txt">{navWordmarkLabel}</span>
    </button>
  ) : (
    <span className="mte-tpl-nav-wordmark-txt">{navWordmarkLabel}</span>
  );
  // `contact === undefined` means contact_page has not settled yet (Phase 1a
  // unlocks before Contact). Keep the tab visible until we know enabled/null,
  // so Contact does not vanish after home paint and reappear later.
  const contactTabVisible =
    CREATOR.contact === undefined
      ? true
      : Boolean(CREATOR.contact?.enabled);
  const allLinks: [string, string, (e: React.MouseEvent) => void][] = [
    ["Home", "home", go("home", "Home")],
    ["Shop", "shop", go("shop", "Products")],
    ["Blog", "blog", go("blog", "Blog")],
    ["Content", "content", go("content", "Content")],
    ["Link in Bio", "linkInBio", go("linkInBio", "Link in Bio")],
    ["About", "about", go("about", "About")],
    ["Contact", "contact", go("contact", "Contact")],
    ...customPages
      .filter((p) => p.inNav)
      .map(
        (p): [string, string, (e: React.MouseEvent) => void] => [
          p.label,
          p.id,
          go(p.id, p.label),
        ],
      ),
  ];
  const omit = new Set(parseNavOmit(get(NAV_OMIT_EDIT_KEY, "")));
  const visibilityFiltered = allLinks.filter(
    ([, k]) =>
      !isBlogNavChromeLocked(k) &&
      k !== "search" &&
      (k === "home" || !isNavOmitted(k, omit)) &&
      (k !== "contact" || contactTabVisible),
  );
  const order = parseNavOrder(get(NAV_ORDER_EDIT_KEY, "")).filter(
    (k) => !isBlogNavChromeLocked(k) && (k === "home" || !isNavOmitted(k, omit)),
  );
  const byKey = new Map(visibilityFiltered.map((row) => [row[1], row]));
  const links = [
    ...order
      .map((k) => byKey.get(k))
      .filter((row): row is [string, string, (e: React.MouseEvent) => void] => Boolean(row)),
    ...visibilityFiltered.filter(([, k]) => !order.includes(k)),
  ];
  const searchVisible = !isNavOmitted("search", omit);
  const searchActive = isNavActive("search");
  const searchLabel = resolveNavLabel(get, "search", "Search");
  const usernameNorm = (CREATOR.bio.username ?? "").trim().toLowerCase();
  /** Prefer canonical `/{user}/...` (or `/website/...` alias) — never bare `/blog`. */
  const navHrefForKey = React.useCallback(
    (k: string) => {
      const custom = customPages.find((p) => p.id === k);
      const fallback = custom
        ? `/${String(custom.slug || "").replace(/^\//, "")}`
        : defaultSlugForNavKey(k);
      const stored = resolveNavHref(get, k, fallback).trim();
      const onWebsite = isPublicWebsiteTemplateRoute(location.pathname);
      if (onWebsite && usernameNorm) {
        const websitePrefix = `/website/${usernameNorm}`;
        // Legacy stored hrefs from before canonical `/{user}` public home.
        if (
          stored === websitePrefix ||
          stored.startsWith(`${websitePrefix}/`)
        ) {
          const rest = stored.slice(websitePrefix.length).replace(/^\/+/, "");
          return getCreatorSpaRelativePath({
            username: usernameNorm,
            route: rest || undefined,
            pathname: location.pathname,
          });
        }
        if (
          !stored ||
          stored === fallback ||
          /^\/(home|shop|blog|about|contact|content|search|press|newsletter|link-in-bio)(\/|$)/i.test(
            stored,
          )
        ) {
          const rest = fallback.replace(/^\//, "");
          return getCreatorSpaRelativePath({
            username: usernameNorm,
            route: rest || undefined,
            pathname: location.pathname,
          });
        }
        // Custom page slug (or any other stored path) — keep under /{user}/…
        if (stored.startsWith("/")) {
          const rest = stored.replace(/^\/+/, "");
          return getCreatorSpaRelativePath({
            username: usernameNorm,
            route: rest || undefined,
            pathname: location.pathname,
          });
        }
      }
      return stored || fallback;
    },
    [get, location.pathname, usernameNorm, customPages],
  );
  const desktopLayout = resolveNavDesktopLayout(style);
  const mobileLayout = resolveNavMobileLayout(mobileStyle);
  const navOrderLive = parseNavOrder(get(NAV_ORDER_EDIT_KEY, ""));
  const navOmitLive = parseNavOmit(get(NAV_OMIT_EDIT_KEY, ""));
  const siteHeaderEditing =
    editable &&
    (editorCtx?.inspector?.section === "Site Header" ||
      editorCtx?.inspector?.view === "navigation");
  // Mobile preview: keep the menu open while Site Navigation is edited so layout
  // and reorder changes paint live on the canvas. Leaving narrow layout must
  // clear the hamburger — desktop still portals the drawer when drawerOpen.
  React.useEffect(() => {
    if (!useMobileNavChrome) {
      setMobileMenuOpen(false);
      return;
    }
    if (!siteHeaderEditing) return;
    setMinimalOpen(false);
    setMobileMenuOpen(true);
  }, [
    siteHeaderEditing,
    useMobileNavChrome,
    mobileLayout,
    mobileStyle,
    navOrderLive.join("|"),
    navOmitLive.join("|"),
  ]);
  const { visible: inlineLinks, overflow: overflowLinks } =
    desktopLayout === "minimal"
      ? { visible: links, overflow: [] as typeof links }
      : splitNavOverflowWithBudget(
          links,
          ([l, k]) =>
            clampNavLabel(
              resolveNavLabel(
                get,
                k,
                SITE_HEADER_NAV_DEFAULTS[k as SiteHeaderNavKey]?.label || l,
              ),
            ),
          navBudgetPx,
        );
  const socials = CREATOR.bio?.social ?? [];
  const AFFILIATE_PLATFORMS = new Set(["Amazon", "ShopMy", "LTK"]);
  const followSocials = socials.filter((s) => !AFFILIATE_PLATFORMS.has(s.platform));
  const shopAffiliates = socials.filter((s) => AFFILIATE_PLATFORMS.has(s.platform));
  /** Top categories from breadcrumb browse (fallback: product.category). */
  const topCategories = (() => {
    if (shopCategories.length) {
      return shopCategories.slice(0, 8).map((c) => ({
        label: c.label,
        slug: c.slug,
      }));
    }
    const counts = new Map<string, number>();
    for (const p of CREATOR.products || []) {
      const cat = String(p.category || "").trim();
      if (!cat || /^other$/i.test(cat)) continue;
      counts.set(cat, (counts.get(cat) || 0) + 1);
    }
    return [...counts.entries()]
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 5)
      .map(([name]) => ({
        label: name,
        slug: name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
      }));
  })();
  // No products → no category column. Side column only if socials/affiliates remain;
  // otherwise pages run full width (HTML memo).
  const minimalHasSide =
    topCategories.length > 0 ||
    followSocials.length > 0 ||
    shopAffiliates.length > 0;

  const searchIcon = searchVisible ? (
    onNavigate ? (
      <button
        type="button"
        aria-label={searchLabel}
        title={searchLabel}
        onClick={go("search", searchLabel)}
        className={`mte-tpl-nav-tool-btn mte-tpl-nav-icon-lg ${searchActive ? "mte-tpl-nav-icon-lg--active" : ""}`}
      >
        <IoSearchOutline className="mte-tpl-nav-tool-icon" aria-hidden />
      </button>
    ) : (
      <span className="mte-tpl-nav-tool-btn mte-tpl-nav-icon-lg" title={searchLabel}>
        <IoSearchOutline className="mte-tpl-nav-tool-icon" aria-hidden />
      </span>
    )
  ) : null;
  // PRODUCTS hover mega-menu temporarily disabled — noisy breadcrumb primaries.
  const shopCatsDropdown = null;
  // const shopCatsDropdown =
  //   shopCatsOpen && shopCategories.length > 0 ? (
  //     <div
  //       className="mte-tpl-nav-shop-drop"
  //       onMouseEnter={() => setShopCatsOpen(true)}
  //       role="menu"
  //       aria-label="Product categories"
  //     >
  //       <div
  //         className="mte-tpl-nav-shop-drop-cols"
  //         style={
  //           {
  //             ["--shop-drop-rows" as string]: String(
  //               Math.max(1, Math.ceil((shopCategories.length + 1) / 3)),
  //             ),
  //           } as React.CSSProperties
  //         }
  //       >
  //         <button
  //           type="button"
  //           className="mte-tpl-nav-shop-drop-link mte-tpl-nav-shop-drop-link--all"
  //           role="menuitem"
  //           onClick={goShopAll}
  //         >
  //           Shop all
  //         </button>
  //         {shopCategories.map((c) => (
  //           <button
  //             key={c.slug}
  //             type="button"
  //             className="mte-tpl-nav-shop-drop-link"
  //             role="menuitem"
  //             onClick={goShopCategory(c.slug, c.label)}
  //           >
  //             {c.label}
  //           </button>
  //         ))}
  //       </div>
  //     </div>
  //   ) : null;

  const linkEl = ([l, k, h]: [
    string,
    string,
    (e: React.MouseEvent) => void,
  ], className = "mte-tpl-nav-link") => {
    const live = clampNavLabel(
      resolveNavLabel(
        get,
        k,
        SITE_HEADER_NAV_DEFAULTS[k as SiteHeaderNavKey]?.label || l,
      ),
    );
    const isShop = k === "shop" && className === "mte-tpl-nav-link";
    if (isBlogNavChromeLocked(k)) {
      return (
        <span
          key={k}
          className={`${className} is-disabled`}
          aria-disabled="true"
          title={BLOG_NAV_DISABLED_TIP}
        >
          {live}
        </span>
      );
    }
    const el = (
      <EL
        key={isShop ? undefined : k}
        path={`nav.${k}`}
        href={navHrefForKey(k)}
        onClick={(e) => {
          closeDrawer();
          closeOverflow();
          if (isShop) {
            goShopAll(e);
            return;
          }
          h(e);
        }}
        // Hover categories disabled — PRODUCTS just navigates to shop.
        onMouseEnter={() => closeShopCats()}
        {...(editable ? {} : { navigateOnClick: true })}
        className={`${className} ${isNavActive(k) ? `${className}--active` : ""}`}
      >
        {live}
      </EL>
    );
    // Only wrap PRODUCTS — wrapping every link breaks nav flex alignment.
    if (!isShop) return el;
    return (
      <span key={k} className="mte-tpl-nav-shop-hit">
        {el}
      </span>
    );
  };
  const moreChip =
    overflowLinks.length > 0 ? (
      <span className={`mte-tpl-nav-more${overflowOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className={`mte-tpl-nav-link mte-tpl-nav-more-btn${overflowOpen ? " is-open" : ""}`}
          aria-expanded={overflowOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMobileMenuOpen(false);
            setMinimalOpen(false);
            setOverflowOpen((open) => !open);
          }}
        >
          More <span className="mte-tpl-nav-more-caret" aria-hidden>▾</span>
        </button>
        {overflowOpen ? (
          <span
            className="mte-tpl-nav-more-menu"
            onClick={(e) => e.stopPropagation()}
          >
            {overflowLinks.map((row) => linkEl(row, "mte-tpl-nav-more-item"))}
          </span>
        ) : null}
      </span>
    ) : null;

  const drawerPortalHost =
    typeof document === "undefined"
      ? null
      : (editable
          ? document.querySelector(".mte-root--editor .mte-bc-frame")
          : null) ?? document.body;

  /** Right-side drawer body shared by mobile and Minimal. */
  const renderRightDrawer = ({
    isFull,
    drawerLinks,
    showMinimalExtras,
  }: {
    isFull: boolean;
    drawerLinks: typeof links;
    showMinimalExtras?: boolean;
  }) => {
    if (!drawerOpen || typeof document === "undefined" || !drawerPortalHost) {
      return null;
    }
    const n = drawerLinks.length;
    const fullFs =
      n <= 6 ? 26 : n <= 8 ? 22 : n <= 10 ? 19 : n <= 12 ? 17 : n <= 14 ? 15 : 14;
    const fullPad =
      n <= 6 ? 11 : n <= 8 ? 9 : n <= 10 ? 7.5 : n <= 12 ? 6 : n <= 14 ? 5 : 4.5;
    const drawerFs = n <= 6 ? 15 : n <= 9 ? 14 : n <= 12 ? 13 : 12;
    const drawerPad = n <= 6 ? 12 : n <= 9 ? 10 : n <= 12 ? 8.5 : 7;
    const menuLinkClass = (k: string) =>
      isFull
        ? `mte-tpl-nav-full-link ${isNavActive(k) ? "mte-tpl-nav-full-link--active" : ""}`
        : `mte-tpl-nav-drawer-link ${isNavActive(k) ? "mte-tpl-nav-drawer-link--active" : ""}`;
    const menuLink = ([l, k]: [string, string]) => {
      const live = clampNavLabel(
        resolveNavLabel(
          get,
          k,
          SITE_HEADER_NAV_DEFAULTS[k as SiteHeaderNavKey]?.label || l,
        ),
      );
      const style = isFull
        ? { fontSize: fullFs, padding: `${fullPad}px 0` }
        : { fontSize: drawerFs, padding: `${drawerPad}px 0` };
      if (isBlogNavChromeLocked(k)) {
        return (
          <span
            key={k}
            className={`${menuLinkClass(k)} is-disabled`}
            style={style}
            aria-disabled="true"
            title={BLOG_NAV_DISABLED_TIP}
          >
            {live}
          </span>
        );
      }
      if (!onNavigate) {
        return (
          <span key={k} className={menuLinkClass(k)} style={style}>
            {live}
          </span>
        );
      }
      if (k === "shop") {
        return (
          <EL
            key={k}
            path={`nav.${k}`}
            href={navHrefForKey(k)}
            onClick={goShopAll}
            {...(editable ? {} : { navigateOnClick: true })}
            className={menuLinkClass(k)}
            style={style}
          >
            {live}
          </EL>
        );
      }
      return (
        <EL
          key={k}
          path={`nav.${k}`}
          href={navHrefForKey(k)}
          onClick={(e) => {
            closeDrawer();
            const targetPage = linkKeyToPage[k] || k;
            if (targetPage === page || k === page) {
              e.preventDefault();
              return;
            }
            go(k, live)(e);
          }}
          {...(editable ? {} : { navigateOnClick: true })}
          className={menuLinkClass(k)}
          style={style}
        >
          {live}
        </EL>
      );
    };
    return createPortal(
      <div
        className={`mte-tpl-nav-portal${isFull ? " mte-tpl-nav-portal--full" : ""}`}
        style={tplThemeVars(T) as React.CSSProperties}
      >
        {isFull ? null : (
          <div
            role="presentation"
            className="mte-tpl-nav-overlay"
            onClick={closeDrawer}
          />
        )}
        <div
          role="dialog"
          aria-modal
          aria-label="Site menu"
          className={isFull ? "mte-tpl-nav-full" : "mte-tpl-nav-drawer"}
        >
          <div className="mte-tpl-nav-drawer-close-row">
            <span />
            <button
              type="button"
              aria-label="Close menu"
              onClick={closeDrawer}
              className="mte-tpl-nav-drawer-close"
            >
              <IoClose size={20} aria-hidden />
            </button>
          </div>
          <nav
            aria-label="Site menu links"
            className={isFull ? "mte-tpl-nav-full-list" : "mte-tpl-nav-drawer-list"}
          >
            {showMinimalExtras ? (
              <div className="mte-tpl-nav-min-head">Pages</div>
            ) : null}
            {drawerLinks.map(([l, k]) => menuLink([l, k]))}
            {showMinimalExtras && minimalHasSide ? (
              <div className="mte-tpl-nav-drawer-extras">
                {topCategories.length > 0 ? (
                  <>
                    <div className="mte-tpl-nav-min-head is-spaced">
                      Top categories
                    </div>
                    {topCategories.map((cat) => (
                      <button
                        key={cat.slug || cat.label}
                        type="button"
                        className="mte-tpl-nav-min-sub"
                        onClick={goShopCategory(cat.slug, cat.label)}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </>
                ) : null}
                {followSocials.length > 0 ? (
                  <>
                    <div className="mte-tpl-nav-min-head is-spaced">
                      Follow
                    </div>
                    <div className="mte-tpl-nav-min-chips">
                      {followSocials.map((s) =>
                        s.url && s.url !== "#" ? (
                          <a
                            key={s.platform}
                            className="mte-tpl-nav-min-chip"
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              templateAnalytics?.trackSocialLink({
                                platform: s.platform,
                                url: s.url,
                                sectionId: "nav",
                                sectionType: "Nav",
                              })
                            }
                          >
                            {s.platform}
                          </a>
                        ) : (
                          <span
                            key={s.platform}
                            className="mte-tpl-nav-min-chip"
                          >
                            {s.platform}
                          </span>
                        ),
                      )}
                    </div>
                  </>
                ) : null}
                {shopAffiliates.length > 0 ? (
                  <>
                    <div className="mte-tpl-nav-min-head is-spaced-sm">
                      Shop with me
                    </div>
                    <div className="mte-tpl-nav-min-chips">
                      {shopAffiliates.map((s) =>
                        s.url && s.url !== "#" ? (
                          <a
                            key={s.platform}
                            className="mte-tpl-nav-min-chip"
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              templateAnalytics?.trackSocialLink({
                                platform: s.platform,
                                url: s.url,
                                sectionId: "nav",
                                sectionType: "Nav",
                              })
                            }
                          >
                            {s.platform}
                          </a>
                        ) : (
                          <span
                            key={s.platform}
                            className="mte-tpl-nav-min-chip"
                          >
                            {s.platform}
                          </span>
                        ),
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </nav>
        </div>
      </div>,
      drawerPortalHost,
    );
  };

  if (useMobileNavChrome) {
    // Every visible page — no More menu; search stays an icon in the top bar only.
    const mobileLinks = links.filter(([, k]) => k !== "search");
    const isFull = mobileLayout === "fullscreen";
    const mobileGo = (k: string, label: string) => (e: React.MouseEvent) => {
      closeDrawer();
      const targetPage = linkKeyToPage[k] || k;
      if (targetPage === page || k === page) {
        e.preventDefault();
        return;
      }
      go(k, label)(e);
    };
    return (
      <>
        <header
          ref={navHeaderRef}
          className="mte-tpl-nav-header mte-tpl-nav-row-mob"
        >
          <div className="mte-tpl-nav-wordmark">{navWordmark}</div>
          <div className="mte-tpl-nav-tools">
            {searchVisible ? (
              onNavigate ? (
                <button
                  type="button"
                  aria-label="Search"
                  onClick={mobileGo("search", "Search")}
                  className={`mte-tpl-nav-tool-btn mte-tpl-nav-icon-lg ${isNavActive("search") ? "mte-tpl-nav-icon-lg--active" : ""}`}
                >
                  <IoSearchOutline className="mte-tpl-nav-tool-icon" aria-hidden />
                </button>
              ) : (
                <span className="mte-tpl-nav-tool-btn mte-tpl-nav-icon-lg">
                  <IoSearchOutline className="mte-tpl-nav-tool-icon" aria-hidden />
                </span>
              )
            ) : null}
            <button
              type="button"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              onClick={() => {
                setMinimalOpen(false);
                setOverflowOpen(false);
                setMobileMenuOpen((o) => !o);
                if (!mobileMenuOpen) {
                  window.requestAnimationFrame(() => {
                    templateAnalytics?.trackSectionEvent(
                      TEMPLATE_ANALYTICS_EVENTS.MOBILE_MENU,
                      { sectionId: "nav", sectionType: "Nav", entityType: "nav" },
                    );
                  });
                }
              }}
              className={
                mobileMenuOpen
                  ? "mte-tpl-nav-tool-btn mte-tpl-nav-menu-btn mte-tpl-nav-menu-btn--open"
                  : "mte-tpl-nav-tool-btn mte-tpl-nav-menu-btn"
              }
            >
              {mobileMenuOpen ? (
                <IoClose className="mte-tpl-nav-tool-icon" aria-hidden />
              ) : (
                <IoMenu className="mte-tpl-nav-tool-icon" aria-hidden />
              )}
            </button>
          </div>
        </header>
        {renderRightDrawer({ isFull, drawerLinks: mobileLinks })}
      </>
    );
  }

  if (desktopLayout === "stacked") {
    return (
      <>
        <header
          className="mte-tpl-nav-header mte-tpl-nav-centered"
          ref={navHeaderRef}
          onMouseLeave={closeShopCats}
        >
          <div className="mte-tpl-nav-wordmark mte-tpl-nav-wordmark--center">
            {navWordmark}
          </div>
          <nav className="mte-tpl-nav-centered-links" ref={navLinksRef} aria-label="Primary">
            {inlineLinks.map((row) => linkEl(row))}
            {moreChip}
            {searchIcon}
          </nav>
          {shopCatsDropdown}
        </header>
        {renderRightDrawer({
          isFull: false,
          drawerLinks: links.filter(([, k]) => k !== "search"),
        })}
      </>
    );
  }

  if (desktopLayout === "minimal") {
    return (
      <>
        <header className={`mte-tpl-nav-header mte-tpl-nav-minimal${minimalOpen ? " is-open" : ""}`}>
          <div className="mte-tpl-nav-minimal-bar">
            <div className="mte-tpl-nav-wordmark">{navWordmark}</div>
            <div className="mte-tpl-nav-tools">
              {searchIcon}
              <button
                type="button"
                className={`mte-tpl-nav-tool-btn mte-tpl-nav-menu-btn${minimalOpen ? " mte-tpl-nav-menu-btn--open" : ""}`}
                aria-expanded={minimalOpen}
                aria-label={minimalOpen ? "Close menu" : "Open menu"}
                onClick={() => {
                  setMobileMenuOpen(false);
                  setOverflowOpen(false);
                  setMinimalOpen((open) => !open);
                }}
              >
                {minimalOpen ? (
                  <IoClose className="mte-tpl-nav-tool-icon" aria-hidden />
                ) : (
                  <IoMenu className="mte-tpl-nav-tool-icon" aria-hidden />
                )}
              </button>
            </div>
          </div>
        </header>
        {renderRightDrawer({
          isFull: false,
          drawerLinks: links.filter(([, k]) => k !== "search"),
          showMinimalExtras: true,
        })}
      </>
    );
  }

  if (desktopLayout === "flank") {
    const half = Math.ceil(inlineLinks.length / 2);
    const left = inlineLinks.slice(0, half);
    const right = inlineLinks.slice(half);
    return (
      <>
        <header
          className="mte-tpl-nav-header mte-tpl-nav-underline-grid"
          ref={navHeaderRef}
          onMouseLeave={closeShopCats}
        >
          <nav className="mte-tpl-nav-under-l" aria-label="Primary">
            {left.map((row) => linkEl(row))}
          </nav>
          <div className="mte-tpl-nav-wordmark mte-tpl-nav-wordmark--underline">
            {navWordmark}
          </div>
          <nav className="mte-tpl-nav-under-r" ref={navLinksRef} aria-label="Primary secondary">
            {right.map((row) => linkEl(row))}
            {moreChip}
            {searchIcon}
          </nav>
          {shopCatsDropdown}
        </header>
        {renderRightDrawer({
          isFull: false,
          drawerLinks: links.filter(([, k]) => k !== "search"),
        })}
      </>
    );
  }

  return (
    <>
      <header
        className="mte-tpl-nav-header mte-tpl-nav-split"
        ref={navHeaderRef}
        onMouseLeave={closeShopCats}
      >
        <div className="mte-tpl-nav-wordmark">{navWordmark}</div>
        <nav className="mte-tpl-nav-split-links" ref={navLinksRef} aria-label="Primary">
          {inlineLinks.map((row) => linkEl(row))}
          {moreChip}
          {searchIcon}
        </nav>
        {shopCatsDropdown}
      </header>
      {renderRightDrawer({
        isFull: false,
        drawerLinks: links.filter(([, k]) => k !== "search"),
      })}
    </>
  );
}

NavSection.options = {
  style: {
    kind: "radio",
    label: "Nav style",
    options: ["standard", "flank", "stacked", "minimal"],
    default: "standard",
  },
} as SectionOptions;
